// src/components/LocationPicker.jsx
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import locations from '../data/rwanda-locations.json';

export default function LocationPicker({ value, onChange, compact = false }) {
  const { t, ready: i18nReady } = useTranslation('common');
  const idBase = useId();

  const [province, setProvince] = useState(value.province || '');
  const [district, setDistrict] = useState(value.district || '');
  const [sector, setSector] = useState(value.sector || '');
  const [cell, setCell] = useState(value.cell || '');

  const provs = locations.provinces;
  const dists = province ? provs.find(p=>p.name===province)?.districts||[] : [];
  const sects = district ? dists.find(d=>d.name===district)?.sectors||[] : [];
  const cells = sector ? sects.find(s=>s.name===sector)?.cells||[] : [];

  const update = (fld, val) => {
    onChange({ province, district, sector, cell, [fld]: val });
  };

  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback;
    const result = t(key);
    return result && result !== key ? result : fallback;
  };

  const baseSelectClass =
    'ui-input h-11 appearance-none bg-no-repeat bg-[right_0.85rem_center] bg-[length:0.75rem] pr-9';
  const fieldGapClass = compact ? 'space-y-3' : 'space-y-4';
  const labelClass = compact
    ? 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';

  return (
    <div className={fieldGapClass}>
      <div>
        <label htmlFor={`${idBase}-province`} className={labelClass}>
          {safeT('locationPicker.province.label', 'Province')}
        </label>
        <select
          id={`${idBase}-province`}
          value={province}
          onChange={(e) => {
            setProvince(e.target.value);
            update('province', e.target.value);
          }}
          className={baseSelectClass}
        >
          <option value="">{safeT('locationPicker.province.placeholder', 'Select Province')}</option>
          {provs.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </div>

      {dists.length > 0 && (
        <div>
          <label htmlFor={`${idBase}-district`} className={labelClass}>
            {safeT('locationPicker.district.label', 'District')}
          </label>
          <select
            id={`${idBase}-district`}
            value={district}
            onChange={(e) => {
              setDistrict(e.target.value);
              update('district', e.target.value);
            }}
            className={baseSelectClass}
          >
            <option value="">{safeT('locationPicker.district.placeholder', 'Select District')}</option>
            {dists.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
          </select>
        </div>
      )}

      {sects.length > 0 && (
        <div>
          <label htmlFor={`${idBase}-sector`} className={labelClass}>
            {safeT('locationPicker.sector.label', 'Sector')}
          </label>
          <select
            id={`${idBase}-sector`}
            value={sector}
            onChange={(e) => {
              setSector(e.target.value);
              update('sector', e.target.value);
            }}
            className={baseSelectClass}
          >
            <option value="">{safeT('locationPicker.sector.placeholder', 'Select Sector')}</option>
            {sects.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      )}

      {cells.length > 0 && (
        <div>
          <label htmlFor={`${idBase}-cell`} className={labelClass}>
            {safeT('locationPicker.cell.label', 'Cell')}
          </label>
          <select
            id={`${idBase}-cell`}
            value={cell}
            onChange={(e) => {
              setCell(e.target.value);
              update('cell', e.target.value);
            }}
            className={baseSelectClass}
          >
            <option value="">{safeT('locationPicker.cell.placeholder', 'Select Cell')}</option>
            {cells.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
